import { Video } from '@vimeo/gctl-api/models/components/schemas/video/Video';

/**
 * Custom React hook to determine the status and processing state of a video clip.
 *
 * @param params - An object containing the video clip and loading state.
 * @param params.clip - The video clip object, which may be a live or non-live video, or null/undefined.
 * @param params.isLoading - Optional boolean indicating if the clip is currently loading. Defaults to false.
 * @returns An object containing:
 *   - `videoStatus`: The status of the video clip.
 *   - `isVideoProcessing`: Boolean or null indicating if the video is currently processing.
 *   - `isLoading`: Boolean indicating if the video is loading.
 *
 * For non-live videos, `isVideoProcessing` is true if the status is not 'available'.
 * For live videos, the processing state is determined by the live status.
 * If the clip is loading or not present, `isVideoProcessing` is null.
 */
export function useStatus({
  clip,
  isLoading: isClipLoading = false,
}: {
  clip?: Video | null;
  isLoading?: boolean;
}) {
  if (isClipLoading || !clip) {
    return { videoStatus: clip?.status, isVideoProcessing: null, isLoading: isClipLoading };
  }

  if (clip.type !== 'live') {
    return {
      videoStatus: clip.status,
      isVideoProcessing: clip.status !== 'available',
      isLoading: isClipLoading,
    };
  }

  const { status: liveStatus } = clip.live || {};
  let isVideoProcessing: boolean | null = null;

  switch (liveStatus) {
    case 'archiving':
    case 'archiving_error':
      isVideoProcessing = true;
      break;
    case 'unavailable':
    case 'pending':
    case 'ready':
    case 'streaming':
      isVideoProcessing = false;
      break;
    case 'streaming_error':
    case 'done':
    default:
      isVideoProcessing = clip.status !== 'available';
      break;
  }

  return { videoStatus: clip.status, isVideoProcessing, isLoading: isClipLoading };
}
